import React from 'react';

/**
 * Last-resort error boundary so the app never renders a silent blank page.
 * Common causes: a user's ad-blocker/extension kills an iframe, a third-party
 * script throws, a lazy chunk fails to fetch, etc. We catch it, show a clear
 * actionable message, and let the user retry.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface in dev tools for the user even if extensions are noisy.
    // eslint-disable-next-line no-console
    console.error('[AstraTerminal ErrorBoundary]', error, info);
  }

  handleReload = () => {
    // Clear any potentially bad partner session and reload from scratch.
    try { localStorage.removeItem('astra_partner_token'); } catch { /* ignore */ }
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;
    const msg = (this.state.error && this.state.error.message) || String(this.state.error);
    return (
      <div style={styles.root} data-testid="error-boundary">
        <div style={styles.card}>
          <div style={styles.badge}>SOMETHING WENT WRONG</div>
          <h1 style={styles.title}>We couldn&apos;t load this page</h1>
          <p style={styles.body}>
            This usually happens when a browser extension (ad-blocker, privacy guard) blocks
            a script the page depends on — or the browser has a stale cached copy.
          </p>

          <ol style={styles.list}>
            <li>Try a <strong>hard refresh</strong>: <code style={styles.kbd}>Ctrl / ⌘ + Shift + R</code></li>
            <li>If that doesn&apos;t help, open the page in an <strong>Incognito / Private window</strong> (this disables extensions).</li>
            <li>If Incognito works, the culprit is an extension — whitelist <code style={styles.kbd}>accounts.google.com</code> and this domain in your ad-blocker.</li>
          </ol>

          <details style={styles.details}>
            <summary style={styles.summary}>Technical details</summary>
            <pre style={styles.pre}>{msg}</pre>
          </details>

          <div style={styles.actions}>
            <button onClick={this.handleReload} style={styles.btnPrimary}>Reload the page</button>
            <a href="/" style={styles.btnGhost}>← Go home</a>
          </div>
        </div>
      </div>
    );
  }
}

const styles = {
  root: {
    minHeight: '100vh',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 24,
    backgroundColor: '#050507',
    color: '#f8fafc',
    fontFamily: "'Inter', -apple-system, sans-serif",
  },
  card: {
    width: '100%', maxWidth: 520,
    padding: 32,
    borderRadius: 16,
    backgroundColor: '#0c0c10',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), 0 24px 60px rgba(0,0,0,0.55)',
  },
  badge: {
    display: 'inline-block',
    padding: '4px 10px',
    fontSize: 10, fontWeight: 700, letterSpacing: '0.14em',
    color: '#fca5a5',
    backgroundColor: 'rgba(239,68,68,0.12)',
    borderRadius: 999,
    marginBottom: 16,
  },
  title: { margin: '0 0 8px', fontSize: 24, fontWeight: 600, letterSpacing: '-0.02em' },
  body: { margin: '0 0 20px', color: '#a1a1aa', fontSize: 14, lineHeight: 1.6 },
  list: { margin: '0 0 24px', padding: '0 0 0 20px', color: '#a1a1aa', fontSize: 13.5, lineHeight: 1.7 },
  kbd: {
    padding: '1px 6px',
    borderRadius: 5,
    fontSize: 12,
    fontFamily: "'JetBrains Mono', monospace",
    color: '#e4e4e7',
    backgroundColor: 'rgba(255,255,255,0.05)',
    boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.06)',
  },
  details: { marginBottom: 20 },
  summary: { cursor: 'pointer', fontSize: 12, color: '#71717a' },
  pre: {
    marginTop: 8,
    padding: 10,
    borderRadius: 8,
    backgroundColor: 'rgba(0,0,0,0.35)',
    color: '#a7c7e0',
    fontSize: 11.5,
    fontFamily: "'JetBrains Mono', monospace",
    overflowX: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  actions: { display: 'flex', gap: 10, flexWrap: 'wrap' },
  btnPrimary: {
    padding: '10px 18px',
    borderRadius: 10,
    fontSize: 13, fontWeight: 600,
    color: '#fff',
    border: '1px solid rgba(59,130,246,0.55)',
    background: 'linear-gradient(180deg, #3b82f6 0%, #1d4ed8 100%)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.25), 0 4px 14px rgba(29,78,216,0.3)',
    cursor: 'pointer',
  },
  btnGhost: {
    padding: '10px 18px',
    borderRadius: 10,
    fontSize: 13, fontWeight: 500,
    color: '#e4e4e7',
    border: '1px solid rgba(255,255,255,0.08)',
    background: 'transparent',
    textDecoration: 'none',
    display: 'inline-flex', alignItems: 'center',
  },
};
